import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSearchOverlay } from '../../contexts/SearchOverlayContext';
import { POPULAR_SEARCHES } from '../../utils/search/popularSearches';
import { Icon } from '../header/draft/Icon';
import './search-overlay.css';

const BG_RGB = '30, 30, 38';

const panelSpring = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 34,
  mass: 0.82,
};

const PANEL_ENTER_DELAY = 0.1;
const BLUR_RAMP_DELAY = 0.34;
const BLUR_RAMP_DURATION = 0.58;

const backdropInitial = {
  backgroundColor: `rgba(${BG_RGB}, 0)`,
  backdropFilter: 'blur(0px) saturate(100%)',
  WebkitBackdropFilter: 'blur(0px) saturate(100%)',
} as const;

const backdropAnimate = {
  backgroundColor: `rgba(${BG_RGB}, 0.52)`,
  backdropFilter: 'blur(40px) saturate(150%)',
  WebkitBackdropFilter: 'blur(40px) saturate(150%)',
} as const;

const backdropTransition = {
  backgroundColor: { duration: 0.22, ease: [0.4, 0, 0.2, 1] as const },
  backdropFilter: {
    delay: BLUR_RAMP_DELAY,
    duration: BLUR_RAMP_DURATION,
    ease: [0.25, 0.1, 0.25, 1] as const,
  },
  WebkitBackdropFilter: {
    delay: BLUR_RAMP_DELAY,
    duration: BLUR_RAMP_DURATION,
    ease: [0.25, 0.1, 0.25, 1] as const,
  },
};

const backdropExit = {
  backgroundColor: `rgba(${BG_RGB}, 0)`,
  backdropFilter: 'blur(0px) saturate(100%)',
  WebkitBackdropFilter: 'blur(0px) saturate(100%)',
};

export function SearchOverlay() {
  const { t } = useTranslation();
  const { isOpen, addToAlbumTarget, closeSearch } = useSearchOverlay();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [isComposing, setIsComposing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(
      () => inputRef.current?.focus(),
      (PANEL_ENTER_DELAY + 0.35) * 1000,
    );

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSearch();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, closeSearch]);

  const goToSearchResults = (term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    closeSearch();
    navigate(`/app/search/${encodeURIComponent(trimmed)}`, {
      state: addToAlbumTarget ? { addToAlbum: addToAlbumTarget } : undefined,
    });
  };

  const handlePopularSelect = (labelKey: string) => {
    goToSearchResults(t(labelKey));
  };

  const submitSearch = () => {
    goToSearchResults(query);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isComposing) {
      e.preventDefault();
      submitSearch();
    }
  };

  const listBaseDelay = PANEL_ENTER_DELAY + 0.12;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="search-overlay"
          className="search-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('search.title')}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <motion.button
            type="button"
            className="absolute inset-0"
            aria-label={t('search.close')}
            initial={backdropInitial}
            animate={backdropAnimate}
            exit={backdropExit}
            transition={backdropTransition}
            onClick={closeSearch}
          />

          <motion.div
            className="search-overlay-panel"
            initial={{ scale: 1.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.06, opacity: 0 }}
            transition={{
              ...panelSpring,
              delay: PANEL_ENTER_DELAY,
              opacity: { delay: PANEL_ENTER_DELAY, duration: 0.25 },
            }}
            style={{ transformOrigin: '50% 18%' }}
          >
            <div className="search-overlay-panel__input-wrap">
              <label className="sr-only" htmlFor="search-overlay-input">
                {t('search.label')}
              </label>
              <div className="search-overlay-panel__field">
                <Icon name="search" className="search-overlay-panel__field-icon" />
                <input
                  ref={inputRef}
                  id="search-overlay-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  placeholder={t('search.placeholder')}
                  className="search-overlay-input"
                  autoComplete="off"
                  enterKeyHint="search"
                />
                <button
                  type="button"
                  onClick={submitSearch}
                  disabled={!query.trim()}
                  className="search-overlay-panel__submit"
                >
                  {t('search.submit')}
                </button>
              </div>
            </div>

            <div className="search-overlay-panel__popular-scroll">
              <p className="search-overlay-panel__popular-title">{t('search.popularTitle')}</p>
              <ol className="search-overlay-panel__popular-list">
                {POPULAR_SEARCHES.map((item, index) => (
                  <motion.li
                    key={item.rank}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: listBaseDelay + index * 0.03,
                      duration: 0.28,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handlePopularSelect(item.labelKey)}
                      className="search-popular-row"
                    >
                      <span
                        className={`search-popular-row__rank${
                          item.rank <= 3 ? ' search-popular-row__rank--top' : ''
                        }`}
                      >
                        {item.rank}
                      </span>
                      <span className="search-popular-row__label">{t(item.labelKey)}</span>
                      <Icon name="north" className="search-popular-row__arrow" />
                    </button>
                  </motion.li>
                ))}
              </ol>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
